import React, { FC } from 'react';
import { PageProps } from 'gatsby';
import { Svg } from 'src/infrastructure/gatsby/types/svg-type';
import { Image } from 'src/infrastructure/gatsby/types/image';
import ReactMarkdown from 'markdown-to-jsx';
import LayoutWithSidebar from 'components/layout/with-sidebar';
import SidebarBuilder from 'src/views/components/builders/sidebar-builder';
import { useTranslation } from 'src/views/misc/localization';
import Breadcrumbs from '@shipae/components-sandbox/component/breadcrumbs';
import { calculateBreadcrumbs } from 'views/utils/calculate-breadcrumbs';
import RichText from '@shipae/components-sandbox/component/rich-text';
import {
  StrapiListingPageContext,
} from 'src/infrastructure/gatsby/node/create-pages/strapi-listing-pages/types';
import {
  StrapiListingPage,
} from 'src/infrastructure/gatsby/types/strapi-listing-page';
import ListingBuilder from 'src/views/components/builders/listing-builder';
import {
  StrapiArticles,
} from 'src/infrastructure/gatsby/types/strapi-articles';
import SEO from 'src/views/components/seo-tags';
import {
  LabelsAndMessages,
} from 'src/infrastructure/gatsby/types/label-and-messages';
import PageContextProvider from '../context';
import {
  PageHeader,
  HeaderAndImage,
  ImageEx,
} from './styles';
import {
  gloabalReactMdOptions,
} from '../../components/shared/react-makdown-options';
import { ROUTES } from '../../constants/ROUTES';

type DataProps = {
  page: StrapiListingPage
  articles: StrapiArticles;
  recentSidebarArticles: StrapiArticles;
  labelsAndMessages: Partial<LabelsAndMessages>;
}

type TopProps = {
  showBreadcrumbs: boolean,
  parentTitle: string | null,
  parentPath: string | null,
  title: string,
  header: string,
  image?: Svg & Image,
};
const Top: React.FC<TopProps> = ({
  showBreadcrumbs,
  parentTitle,
  parentPath,
  title,
  header,
  image,
}) => {
  const { locale, t } = useTranslation();

  return (
    <PageHeader>
      {showBreadcrumbs
        ? (
          <Breadcrumbs
            home={{
              label: t('home'),
              url: ROUTES[locale].HOME,
            }}
            links={calculateBreadcrumbs({
              parentTitle,
              parentPath,
              title,
            })}
          />
        ) : null}
      <HeaderAndImage>
        <RichText>
          <ReactMarkdown
            options={gloabalReactMdOptions}
          >
            {header || ''}
          </ReactMarkdown>
        </RichText>
        {image?.localFile?.childImageSharp?.fluid && (
          <ImageEx
            fluid={image?.localFile?.childImageSharp?.fluid}
            alt={image?.alternativeText || ''}
          />
        )}
      </HeaderAndImage>
    </PageHeader>
  );
};

const Template: FC<
  PageProps<DataProps, StrapiListingPageContext>
> = (props) => {
  const {
    location,
    pageContext,
    data: {
      page: {
        title,
        showBreadcrumbs,
        header,
        image,
        pageSettings,
        additionalPageSettings,
        seoSettings,
        sidebarComponents,
        translationLinks = [],
      },
      articles,
      recentSidebarArticles,
      labelsAndMessages,
    },
  } = props;

  if (!seoSettings) {
    throw new Error('No SEO settings provided!');
  }

  return (
    <PageContextProvider
      locale={pageContext.locale}
      translationLinks={translationLinks}
      pageSettings={pageSettings}
      additionalPageSettings={additionalPageSettings}
      title={title}
      labelsAndMessages={labelsAndMessages}
    >
      <LayoutWithSidebar
        location={location}
        top={(
          <Top
            showBreadcrumbs={showBreadcrumbs}
            parentTitle={pageSettings.parentPageTitle}
            parentPath={pageSettings.parentPagePath}
            title={title}
            header={header}
            image={image}
          />
        )}
        main={(
          <ListingBuilder
            articles={articles}
            pageContext={pageContext}
          />
        )}
        sidebar={(
          <SidebarBuilder
            sidebarComponents={sidebarComponents}
            recentSidebarArticles={recentSidebarArticles}
          />
        )}
      >
        <SEO
          metaTitle={seoSettings.metaTitle}
          metaDescription={seoSettings.metaDescription}
          metaIndex={seoSettings.metaIndex}
          metaFollow={seoSettings.metaFollow}
          canonicalUrl={seoSettings.canonicalUrl}
          location={location}
          pageType="website"
          translationLinks={translationLinks}
        />
      </LayoutWithSidebar>
    </PageContextProvider>
  );
};

export default Template;
